<template>
    <div class="c-formFilePicker" style="width: 100%;">
        <QInput ref="inputRef"
                class="caption sform-input-white"
                v-model="inputValue"
                :label="label"
                :hint="hint"
                :hide-hint="true"
                :bottom-slots="showBottomSlots"
                :rules="rules" v-bind="compilerErrorFieldProps"
                outlined
                dense
                autocomplete="off"
                style="width: 100%;"
                @click="onInputClick"
                @update:model-value="handleInput"
                @keydown="onKeydown"
                @focus="onInputFocus"
                @blur="onInputBlur"
        >
            <template #prepend>
                <QIcon name="mdi-folder" />
            </template>
            <template #append>
                <QIcon
                    :name="menu ? 'mdi-menu-up' : 'mdi-menu-down'"
                    class="cursor-pointer c-formFilePicker__dropdown-icon"
                    @click.stop="handleAppendIconClick"
                />
            </template>
        </QInput>
        <QMenu :close-on-content-click="false"
               :close-on-click="true"
               :target="inputTargetEl || true"
               anchor="bottom left"
               self="top left"
               fit
               no-focus
               no-refocus
               max-height="400px"
               :model-value="menu"
               @update:model-value="updateMenuState"
               @show="onMenuShow"
               :offset="[0, 4]">
            <!-- mousedown.prevent keeps focus on the input so typing / arrows keep working -->
            <QList dense @mousedown.prevent>
                <QItem v-for="item in flatItems"
                       :key="item.path"
                       :ref="el => setItemRef(el, item)"
                       clickable
                       :class="[`pad-${item.depth}`, 'q-ml-xs']"
                       :active="item.path === selectedPath"
                       active-class="bg-grey-3 text-dark"
                       @click="handleItemClick(item)">
                    <!-- Chevron expands/collapses; separate from selecting the folder -->
                    <QItemSection side class="c-formFilePicker__expand">
                        <QIcon
                            v-if="item.isFolder"
                            :name="expandedFolders.has(item.path) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                            size="sm"
                            class="cursor-pointer"
                            @click.stop="onExpandClick(item)"
                        />
                    </QItemSection>
                    <QItemSection avatar>
                        <QIcon :name="item.isFolder ? (expandedFolders.has(item.path) ? 'mdi-folder-open' : 'mdi-folder') : 'mdi-file-outline'" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>{{ item.name }}</QItemLabel>
                    </QItemSection>
                    <QItemSection v-if="item.isFolder && allowFolderSelection" side>
                        <QIcon name="mdi-check" size="xs" class="c-formFilePicker__select-hint" />
                    </QItemSection>
                </QItem>
            </QList>
        </QMenu>
    </div>
</template>

<script>
    import { QInput, QMenu, QList, QItem, QItemSection, QItemLabel, QIcon } from 'quasar';
    import utils from '../../../Shared/utils.jsx';

    const TREE_ROOT = '/';
    const MAX_PAGE_SIZE = 1000;

    /**
     * Query value for Apps/Storage/Files/List objectType.
     * A string is one exact type. An array is a JSON array (empty string includes folders).
     * Null, '', and [] mean no filter.
     */
    function objectTypeQueryValue(objectType) {
        if (objectType == null)
            return null;
        if (Array.isArray(objectType)) {
            if (objectType.length === 0)
                return null;
            return JSON.stringify(objectType.map(v => (v == null ? '' : String(v))));
        }
        if (objectType === '')
            return null;
        return String(objectType);
    }

    export default {
        name: 'file-picker-field',
        components: {
            QInput, QMenu, QList, QItem, QItemSection, QItemLabel, QIcon,
        },
        props: {
            modelValue: { type: String, default: '' },
            formatData: { type: Object, default: () => ({}) },
            label: { type: String, default: undefined },
            hint: { type: String, default: undefined },
            rules: { type: Array, default: () => [] },
            compilerErrorFieldProps: { type: Object, default: () => ({}) },
            showBottomSlots: { type: Boolean, default: true },
        },
        emits: ['update:modelValue', 'focus', 'blur'],
        data() {
            return {
                selectedPath: '',
                searchQuery: '',
                fileTree: {},
                expandedFolders: new Set([TREE_ROOT]),
                menu: false,
                isOpeningMenu: false,
                itemRefs: {},
                inputTargetEl: null,
            };
        },
        mounted() {
            this.selectedPath = this.modelValue || '';
            this.searchQuery = this.modelValue || '';
            this.fetchFolder(TREE_ROOT);
        },
        computed: {
            rootPath() {
                const root = this.formatData?.RootPath;
                if (root == null || root === '')
                    return '';
                return String(root).replace(/^\/+|\/+$/g, '');
            },
            objectType() {
                const ot = this.formatData?.ObjectType;
                if (Array.isArray(ot))
                    return ot;
                if (ot == null || ot === '')
                    return null;
                return ot;
            },
            maxRows() {
                const n = this.formatData?.MaxRows;
                return (typeof n === 'number' && n > 0) ? n : 500;
            },
            allowFolderSelection() {
                return !!this.formatData?.AllowFolderSelection;
            },
            /** PlatformFiles (default) | MassStorage — from schema formatData.SourceRepository */
            sourceRepository() {
                const v = this.formatData?.SourceRepository;
                return v === 'MassStorage' ? 'MassStorage' : 'PlatformFiles';
            },
            storageProviderId() {
                const id = this.formatData?.StorageProviderId;
                return (id == null || id === '') ? null : String(id);
            },
            /** Always editable; while the menu is open use searchQuery so typing isn't clobbered by model. */
            inputValue: {
                get() {
                    return this.menu ? this.searchQuery : (this.modelValue || '');
                },
                set(value) {
                    this.searchQuery = value ?? '';
                    if (!this.menu) {
                        this.selectedPath = value ?? '';
                        this.commitValue(value ?? '');
                    }
                }
            },
            flatItems() {
                const items = [];

                const traverse = (path, depth = 0) => {
                    if (!this.expandedFolders.has(path) && path !== TREE_ROOT)
                        return;

                    const folderContents = this.fileTree[path] || [];
                    folderContents.forEach(item => {
                        items.push({ ...item, depth });
                        if (item.isFolder && this.expandedFolders.has(item.path))
                            traverse(item.path, depth + 1);
                    });
                };

                traverse(TREE_ROOT);
                return items;
            },
        },
        methods: {
            commitValue(value) {
                this.$emit('update:modelValue', value ?? '');
            },
            isFolderType(type) {
                return type === 'Folder' || type === 1 || type === '1';
            },
            toApiFolder(path) {
                if (!path || path === TREE_ROOT)
                    return this.rootPath;
                return path.replace(/^\//, '');
            },
            buildMassStorageUrl(apiFolder) {
                let url = apiFolder
                    ? `Apps/Storage/Objects/Children/${apiFolder}`
                    : 'Apps/Storage/Objects/Children/';
                const params = [`maxRows=${this.maxRows}`];
                if (this.storageProviderId)
                    params.push(`storageProviderId=${encodeURIComponent(this.storageProviderId)}`);
                return `${url}?${params.join('&')}`;
            },
            async listMassStorage(apiFolder) {
                const files = await utils.api.get(this.buildMassStorageUrl(apiFolder));
                return Array.isArray(files) ? files : [];
            },
            /**
             * GET Apps/Storage/Files/List. Follows continuationToken until MaxRows or the folder is exhausted.
             * objectType filter is only valid with the default sortBy=name.
             */
            async listPlatformFiles(apiFolder) {
                const cap = this.maxRows;
                const collected = [];
                let continuationToken = null;

                while (collected.length < cap) {
                    const pageSize = Math.min(MAX_PAGE_SIZE, cap - collected.length);
                    const params = [
                        `folder=${encodeURIComponent(apiFolder || '')}`,
                        `pageSize=${pageSize}`,
                    ];
                    const objectType = objectTypeQueryValue(this.objectType);
                    if (objectType != null)
                        params.push(`objectType=${encodeURIComponent(objectType)}`);
                    if (continuationToken)
                        params.push(`continuationToken=${encodeURIComponent(continuationToken)}`);

                    const page = await utils.api.get(`Apps/Storage/Files/List?${params.join('&')}`);
                    const items = Array.isArray(page?.items) ? page.items : [];
                    collected.push(...items);
                    if (!page?.hasMore || !page?.continuationToken)
                        break;
                    continuationToken = page.continuationToken;
                }

                return collected.slice(0, cap);
            },
            async fetchFolder(path) {
                if (this.fileTree[path])
                    return;

                const apiFolder = this.toApiFolder(path);
                try {
                    const files = this.sourceRepository === 'MassStorage'
                        ? await this.listMassStorage(apiFolder)
                        : await this.listPlatformFiles(apiFolder);
                    const list = Array.isArray(files) ? files : [];
                    this.fileTree[path] = list.map(f => ({
                        name: f.FileNameNoPath,
                        path: f.FullNameWithPath,
                        isFolder: this.isFolderType(f.Type),
                    }));
                }
                catch (e) {
                    const target = this.sourceRepository === 'MassStorage'
                        ? this.buildMassStorageUrl(apiFolder)
                        : `Apps/Storage/Files/List?folder=${encodeURIComponent(apiFolder || '')}`;
                    utils.warn(`formFilePicker failed to list ${target}`, e);
                    this.fileTree[path] = [];
                }
            },
            setItemRef(el, item) {
                if (el)
                    this.itemRefs[item.path] = el;
            },
            getNativeInputEl() {
                const root = this.$refs.inputRef?.$el;
                return root?.querySelector?.('input') || null;
            },
            focusNativeInput() {
                const input = this.getNativeInputEl();
                if (input && document.activeElement !== input)
                    input.focus({ preventScroll: true });
            },
            scrollSelectedIntoView() {
                if (!this.selectedPath)
                    return;
                const selectedRef = this.itemRefs[this.selectedPath];
                const selectedEl = selectedRef?.$el || selectedRef;
                if (selectedEl?.scrollIntoView) {
                    selectedEl.scrollIntoView({
                        behavior: 'auto',
                        block: 'nearest',
                        inline: 'start'
                    });
                }
            },
            onMenuShow() {
                this.$nextTick(() => {
                    this.focusNativeInput();
                    this.scrollSelectedIntoView();
                });
            },
            parentFolderPaths(filePath) {
                if (!filePath)
                    return [];

                const root = this.rootPath;
                let relative = filePath;
                if (root) {
                    if (filePath === root)
                        return [];
                    if (filePath.startsWith(root + '/'))
                        relative = filePath.slice(root.length + 1);
                }

                const parts = relative.split('/').filter(Boolean);
                const parents = [];
                let current = root;
                for (let i = 0; i < parts.length - 1; i++) {
                    current = current ? `${current}/${parts[i]}` : parts[i];
                    parents.push(current);
                }
                return parents;
            },
            ensureParentFoldersExpanded() {
                const parents = this.parentFolderPaths(this.selectedPath);
                parents.forEach(p => this.expandedFolders.add(p));
                this.expandedFolders = new Set(this.expandedFolders);
            },
            onInputClick() {
                if (!this.menu)
                    this.openMenu();
            },
            /**
             * @param {{ preserveQuery?: boolean }} [opts]
             * preserveQuery keeps the typed searchQuery (used when typing opens the menu).
             */
            async openMenu(opts = {}) {
                const preserveQuery = !!opts.preserveQuery;
                this.inputTargetEl = this.$refs.inputRef?.$el || this.inputTargetEl;
                this.isOpeningMenu = true;
                this.itemRefs = {};
                this.selectedPath = this.modelValue || this.selectedPath || '';
                if (!preserveQuery && !this.menu)
                    this.searchQuery = this.modelValue || '';
                this.ensureParentFoldersExpanded();
                await this.$nextTick();

                const promises = [this.fetchFolder(TREE_ROOT)];
                for (const folderPath of this.parentFolderPaths(this.selectedPath)) {
                    if (!this.fileTree[folderPath])
                        promises.push(this.fetchFolder(folderPath));
                }
                await Promise.all(promises);

                this.menu = true;
                this.$nextTick(() => {
                    this.focusNativeInput();
                    this.scrollSelectedIntoView();
                });
            },
            updateMenuState(value) {
                if (!value && this.isOpeningMenu) {
                    this.isOpeningMenu = false;
                    return;
                }
                if (this.menu !== value)
                    this.menu = value;
                this.isOpeningMenu = false;

                // Persist typed custom value when the menu closes without a list pick.
                if (!value && this.searchQuery !== (this.modelValue || ''))
                    this.commitValue(this.searchQuery || '');

                if (value)
                    this.$nextTick(() => this.focusNativeInput());
            },
            handleAppendIconClick() {
                if (this.menu)
                    this.menu = false;
                else
                    this.openMenu();
            },
            async handleInput() {
                const query = (this.searchQuery || '').trim();

                if (!this.menu)
                    await this.openMenu({ preserveQuery: true });

                if (!query)
                    return;

                const q = query.toLowerCase();
                const firstMatch = this.flatItems.find(item =>
                    item.name.toLowerCase().startsWith(q)
                    || item.path.toLowerCase().startsWith(q)
                );
                if (firstMatch) {
                    this.selectedPath = firstMatch.path;
                    this.$nextTick(() => this.scrollSelectedIntoView());
                }
            },
            onKeydown(e) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!this.menu)
                        this.openMenu().then(() => this.navigateDown());
                    else
                        this.navigateDown();
                }
                else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.menu)
                        this.navigateUp();
                }
                else if (e.key === 'Enter') {
                    e.preventDefault();
                    this.handleEnter();
                }
            },
            handleEnter() {
                if (this.menu && this.selectedPath) {
                    const item = this.flatItems.find(i => i.path === this.selectedPath);
                    if (item) {
                        if (item.isFolder) {
                            if (this.allowFolderSelection)
                                this.commitSelection(item.path);
                            else
                                this.toggleFolder(item);
                            return;
                        }
                        this.commitSelection(item.path);
                        return;
                    }
                }
                // Custom path / free text
                this.commitValue(this.searchQuery || this.inputValue || '');
                this.menu = false;
            },
            navigateUp() {
                if (!this.menu || !this.flatItems.length)
                    return;
                const currentIndex = this.flatItems.findIndex(item => item.path === this.selectedPath);
                const nextIndex = currentIndex > 0
                    ? currentIndex - 1
                    : this.flatItems.length - 1;
                this.selectedPath = this.flatItems[nextIndex].path;
                this.$nextTick(() => this.scrollSelectedIntoView());
            },
            navigateDown() {
                if (!this.menu || !this.flatItems.length)
                    return;
                const currentIndex = this.flatItems.findIndex(item => item.path === this.selectedPath);
                const nextIndex = currentIndex < this.flatItems.length - 1 && currentIndex >= 0
                    ? currentIndex + 1
                    : 0;
                this.selectedPath = this.flatItems[nextIndex].path;
                this.$nextTick(() => this.scrollSelectedIntoView());
            },
            commitSelection(path) {
                this.selectedPath = path;
                this.searchQuery = path;
                this.commitValue(path);
                this.menu = false;
            },
            onInputFocus() {
                this.$emit('focus');
            },
            onInputBlur() {
                // While opening, Quasar may briefly blur the field — put focus back.
                if (this.isOpeningMenu) {
                    this.$nextTick(() => this.focusNativeInput());
                    return;
                }
                // Menu stays open with focus on the input (no-focus + mousedown.prevent).
                // Defer commit to menu-close / true leave.
                if (this.menu)
                    return;
                if (this.searchQuery !== (this.modelValue || ''))
                    this.commitValue(this.searchQuery || '');
                this.$emit('blur');
            },
            async toggleFolder(item) {
                this.selectedPath = item.path;
                if (this.expandedFolders.has(item.path)) {
                    this.expandedFolders.delete(item.path);
                    this.expandedFolders = new Set(this.expandedFolders);
                    this.$nextTick(() => { this.menu = true; });
                }
                else {
                    this.expandedFolders.add(item.path);
                    await this.fetchFolder(item.path);
                    this.expandedFolders = new Set(this.expandedFolders);
                    this.$nextTick(() => { this.menu = true; });
                }
            },
            async onExpandClick(item) {
                await this.toggleFolder(item);
            },
            async handleItemClick(item) {
                this.selectedPath = item.path;
                if (item.isFolder) {
                    // With folder selection: row click picks the folder; chevron drills in.
                    // Without: row click drills in (same as chevron).
                    if (this.allowFolderSelection)
                        this.commitSelection(item.path);
                    else
                        await this.toggleFolder(item);
                }
                else {
                    this.commitSelection(item.path);
                }
            },
        },
    };
</script>

<style scoped>
    .q-item {
        min-height: 36px !important;
    }

    .q-item__label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .c-formFilePicker__expand {
        min-width: 24px !important;
        padding-right: 0 !important;
    }

    /* Subtle affordance: check appears on hover / when highlighted */
    .c-formFilePicker__select-hint {
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    .q-item:hover .c-formFilePicker__select-hint,
    .q-item.q-item--active .c-formFilePicker__select-hint {
        opacity: 0.55;
    }

    .pad-0 {
        padding-left: 0rem !important;
    }
    .pad-1 {
        padding-left: 1rem !important;
    }
    .pad-2 {
        padding-left: 2rem !important;
    }
    .pad-3 {
        padding-left: 3rem !important;
    }
    .pad-4 {
        padding-left: 4rem !important;
    }
    .pad-5 {
        padding-left: 5rem !important;
    }
    .pad-6 {
        padding-left: 5rem !important;
    }
    .pad-7 {
        padding-left: 5rem !important;
    }
    .pad-8 {
        padding-left: 5rem !important;
    }
    .pad-9 {
        padding-left: 5rem !important;
    }

    .c-formFilePicker__dropdown-icon {
        transition: transform 0.2s ease-in-out;
    }
</style>
